/**
 * exercise-edit-detection.js
 */

import React from 'react';
import {
  Box,
  Grid,
  Alert
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { parseISO, formatISO9075, isValid, addYears } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import DetectionOdorsEdit from 'components/detection-odors/edit';
import Autocomplete from 'components/form-inputs/autocomplete';
import DateTimePicker from 'components/form-inputs/date-time-picker';


export default function ExerciseEditDetection(props) {
  const {
    storedRecord,
    drawerState,
    translation,
    options,
  } = props;
  const { t } = useTranslation(translation);
  const formContext = useFormContext();
  const { control } = formContext;
  const workModeValue = useWatch({ control, name: 'workMode' });
  const isControlledNegative = useWatch({ control, name: 'isControlledNegative' });
  const detectionEnvironments = useWatch({ control, name: 'detection.environments'});

 
  return (
    <>
      <Box display={workModeValue === Types.WorkMode.DETECTION ? 'block' : 'none'}>
        {/* shift detection editor left on mobile for space requirements */}
        <Box 
          sx={theme => ({ 
            [theme.breakpoints.down('sm')]: {
              '& .pt-icon-section-content': {
                marginLeft: '-32px',
                maxWidth: `calc(100vw - 40px)`,
              }
            },
          })}
        >
          {storedRecord?.applicableHandlerDogs?.length === 1 && storedRecord?.applicableHandlerDogs?.[0]?.hasCompletions &&
          <Alert severity='warning' sx={{ mb: 3 }}>
            You've already completed this exercise. If you make odor modifications then that may change which odors 
            are designated as target and proofing. Consider reviewing each completion record to verify the results.
          </Alert>}

          <EditorIconSection
            iconName='environment'
            iconMarginTop={6}
            helpProps={{
              translation,
              section: 'environments'
            }}
          >
            <DetectionOdorsEdit
              name='detection.environments'
              recordType={Types.Record.EXERCISE}
              drawerState={drawerState}
              translation={translation}
              options={options}
            />

            { detectionEnvironments?.length > 0 && isControlledNegative && 
            <Alert severity="warning" style={{marginTop: 32}}>This exercise will be recorded as a <strong>Controlled Negative</strong> if target odors aren't provided.</Alert> }

            {/* optional: odor placed by, set time */}
            <EditorAccordion
              highlight
              name='Detection Optional'
              style={{ marginTop: 8 }}
              data-testid='exercise-edit-odor-placement-accordion'

              summary={<>
                <FormSectionTitle
                  style={shouldRenderMobileLayout() ? { marginLeft: '32px' } : {}}
                >
                  {window.innerWidth < 450 ? 'Optional Fields' : 'OPTIONAL: Odor Placement'}
                </FormSectionTitle>
              </>}

              detail={
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {/* odor placed by */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('detection.odorPlacedBy.helpTitle')}
                      text={t('detection.odorPlacedBy.helpInfo')}
                    >
                      {focusHandlers =>
                        <Autocomplete
                          name='detection.odorPlacedBy'
                          label={t('detection.odorPlacedBy.label')}
                          options={options.odorPlacers.map(o => o.name)}
                          focusHandlers={focusHandlers}
                          rules={{
                            maxLength: {
                              value: 100,
                              message: t('detection.odorPlacedBy.errMsgLength')
                            }
                          }}
                        />
                      }
                    </HelpTextItem>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    {/* odor place time */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('detection.odorPlacementTime.helpTitle')}
                      text={t('detection.odorPlacementTime.helpInfo')}
                    >
                      {focusHandlers =>
                        <DateTimePicker
                          variant='datetime'
                          name='detection.odorPlacementTime'
                          label='Odor Placement Time'
                          focusHandlers={focusHandlers}
                          clearable
                          defaultValue={parseISO(storedRecord.eventDate)}
                          format='MM/dd/yyyy HH:mm'
                          minDate={addYears(parseISO(storedRecord.eventDate), -1)}
                          setValueAs={date => {
                            if (date && isValid(date)) {
                              return formatISO9075(date); // no-timezone string
                            }
                            return date;
                          }}
                        />
                      }
                    </HelpTextItem>
                  </Grid>
                </Grid>}

            />
          </EditorIconSection>
        </Box>
      </Box>
    </>
  );
}